/**
 * What a link pasted into «Открыть по ссылке» points at. The app shares a project as
 * `https://homeplannerapp.com/all/project/<id>` (its ShareUrl since 2026-09-21, also under
 * `/kitchen/`), older builds as `https://share.roomplannerapp.com/all/project/<id>`, e-mails as
 * `…/open/project/<id>`, the landing's button as `icandesign://project/<id>`, and this editor's
 * own address carries `?project=<id>`. Each names the project by its catalog id, which is all
 * the catalog needs to hand out the file. An invitation names a token instead: the multiplayer
 * link (`https://icandesignapp.com/all/collab/<token>`, `icandesign://collab/share/<token>`),
 * a share or e-mail invitation, which puts its UUID in the project slot (`…/all/project/<uuid>`),
 * or this editor's own address of one (`#collab=<token>`). The editor opens an invitation view-only.
 */
export type ProjectLink = { kind: 'project'; id: string } | { kind: 'invite'; token: string };

const PROJECT_ID = /^[0-9a-f]{24}$/i;
const INVITE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TOKEN = /^[A-Za-z0-9_-]{8,128}$/;
/** The app's share, landing and API hosts, the fallback domain among them, and their subdomains. */
const HOSTS = /(^|\.)(homeplannerapp\.com|roomplannerapp\.com|icandesignapp\.com|icandesign\.app)$/i;

export function readProjectLink(text: string): ProjectLink | null {
  const raw = text.trim();
  if (PROJECT_ID.test(raw)) return { kind: 'project', id: raw.toLowerCase() };
  let url: URL;
  try { url = new URL(raw); } catch { return null; }
  let parts: string[];
  if (url.protocol === 'icandesign:') parts = [url.hostname, ...url.pathname.split('/')];
  else if ((url.protocol === 'https:' || url.protocol === 'http:') && (HOSTS.test(url.hostname) || isThisSite(url))) parts = url.pathname.split('/');
  else return null;
  parts = parts.filter(Boolean).map((part) => { try { return decodeURIComponent(part); } catch { return part; } });
  const project = (id: string | undefined): ProjectLink | null => id && PROJECT_ID.test(id) ? { kind: 'project', id: id.toLowerCase() } : null;
  const invite = (token: string | undefined): ProjectLink | null => token && TOKEN.test(token) ? { kind: 'invite', token } : null;
  const slot = (id: string | undefined): ProjectLink | null => id && INVITE_UUID.test(id) ? { kind: 'invite', token: id } : project(id);
  const [first, second, third] = parts;
  // `/<section>/project/<id>` and `/open/project/<id>`; `icandesign://project/<id>`.
  if (second === 'project' && parts.length === 3) return slot(third);
  if (first === 'project' && parts.length === 2) return slot(second);
  // `/projects/<id>` and `/projects/<id>/project`, the catalog's own addresses.
  if (first === 'projects' && (parts.length === 2 || (parts.length === 3 && third === 'project'))) return project(second);
  // `/<section>/collab/<token>`, `/collab/share/<token>`, `icandesign://collab/share/<token>`.
  if (second === 'collab' && parts.length === 3) return invite(third);
  if (first === 'collab' && second === 'share' && parts.length === 3) return invite(third);
  // This editor's own link: `?project=<id>`, or an invitation in the fragment (`#collab=<token>`), which wins as in the editor.
  const collab = new URLSearchParams(url.hash.slice(1)).get('collab');
  return collab !== null ? invite(collab) : project(url.searchParams.get('project') ?? undefined);
}

function isThisSite(url: URL): boolean {
  return typeof location !== 'undefined' && url.host === location.host;
}
